import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AssetDomain, FinancialItem, FinancialRating } from '../types';
import {
  EntityDocumentsPanel,
  type EntityDocumentUploadControl,
} from '../components/EntityDocumentsPanel';
import { FinancialItemForm } from '../components/FinancialItemForm';
import GlobalEmptyState from '../components/GlobalEmptyState';
import GlobalSidebar from '../components/GlobalSidebar';
import GlobalTabs from '../components/GlobalTabs';
import { GlobalDropdown, type GlobalDropdownOption } from '../components/GlobalDropdown';
import { GlobalSortableTh } from '../components/GlobalSortableTh';
import { GlobalButton } from '../components/GlobalButton';
import { useTableSort } from '../hooks/useTableSort';
import { StatCard } from '../components/StatCard';
import { SortableStatGrid } from '../components/SortableStatGrid';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { categoryLabel } from '../lib/assetCategories';
import { isAssetCategory, legacyCategoryToAssetCategory } from '../lib/assetRegistryNormalize';
import { ratingClass } from '../utils/ratings';
import { formatEuro, formatPercentPoints, formatSignedEuro } from '../utils/formatters';
import { financialPositionSummary } from '../utils/calculations';
import { buildFinancialsKpiExplains } from '../lib/kpiExplainBuilders';
import { newId } from '../utils/id';
import '../App.css';

type Filter = 'all' | 'asset' | 'liability';
type DomainFilter = 'all' | AssetDomain;
type FinancialSortKey = 'name' | 'domain' | 'category' | 'value' | 'monthlyChange' | 'rating';

const RATING_SORT_ORDER: Record<FinancialRating, number> = {
  excellent: 5,
  good: 4,
  neutral: 3,
  bad: 2,
  critical: 1,
};

function itemDomain(item: FinancialItem): AssetDomain {
  return item.domain ?? 'personal';
}

function filterByDomain(items: FinancialItem[], domain: DomainFilter): FinancialItem[] {
  if (domain === 'all') return items;
  return items.filter((i) => itemDomain(i) === domain);
}

function domainLabel(domain: AssetDomain): string {
  return domain === 'business' ? 'Business' : 'Personal';
}

function globalEmptyLeadCopy(domainFilter: DomainFilter): string | null {
  if (domainFilter === 'personal') {
    return 'Start with homes, savings, loans, and other personal holdings.';
  }
  if (domainFilter === 'business') {
    return 'Track company cash, equipment, and business debt in one place.';
  }
  return 'Add assets and liabilities to see totals and net position here.';
}

function domainEmptyCopy(domain: Exclude<DomainFilter, 'all'>): string {
  return domain === 'business'
    ? 'No business assets or liabilities yet.'
    : 'No personal assets or liabilities yet.';
}

function filterEmptyCopy(domainFilter: DomainFilter, filter: Filter): string {
  if (filter === 'liability') {
    if (domainFilter === 'business') return 'No business liabilities';
    if (domainFilter === 'personal') return 'No personal liabilities';
    return 'No liabilities yet';
  }
  if (filter === 'asset') {
    if (domainFilter === 'business') return 'No business assets';
    if (domainFilter === 'personal') return 'No personal assets';
    return 'No assets yet';
  }
  return 'No items match this filter';
}

function itemCategoryLabel(item: FinancialItem): string {
  const category = isAssetCategory(item.category)
    ? item.category
    : legacyCategoryToAssetCategory(item.type, String(item.category));
  return categoryLabel(category);
}

function signedValueClass(value: number): string {
  if (value > 0) return 'financialSnapshot-profit financialSnapshot-profit--positive';
  if (value < 0) return 'financialSnapshot-profit financialSnapshot-profit--negative';
  return '';
}

function assetValueNode(value: number): ReactNode {
  return <span className={signedValueClass(value)}>{formatSignedEuro(value)}</span>;
}

function liabilityValueNode(value: number): ReactNode {
  const amount = Math.abs(value);
  if (amount === 0) return formatEuro(0);
  return (
    <span className="financialSnapshot-profit financialSnapshot-profit--negative">
      −{formatEuro(amount, true)}
    </span>
  );
}

function signedChangeNode(value: number): ReactNode {
  return <span className={signedValueClass(value)}>{formatSignedEuro(value)}</span>;
}

function registryItemValueNode(item: FinancialItem): ReactNode {
  return item.type === 'liability'
    ? liabilityValueNode(item.currentValue)
    : assetValueNode(item.currentValue);
}

function assetsByIdMap(items: FinancialItem[]): Map<string, FinancialItem> {
  const map = new Map<string, FinancialItem>();
  for (const item of items) {
    if (item.type === 'asset') {
      map.set(item.id, item);
    }
  }
  return map;
}

function LinkedAssetLine({
  item,
  assetsById,
  onOpenAsset,
}: {
  item: FinancialItem;
  assetsById: Map<string, FinancialItem>;
  onOpenAsset: (asset: FinancialItem) => void;
}) {
  if (item.type !== 'liability' || !item.linkedAssetId) {
    return null;
  }

  const linked = assetsById.get(item.linkedAssetId);
  const linkedStyle = {
    display: 'block' as const,
    marginTop: 4,
    fontSize: '0.75rem',
    textAlign: 'left' as const,
  };

  if (linked) {
    return (
      <button
        type="button"
        className="muted"
        style={{
          ...linkedStyle,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={(e) => {
          e.stopPropagation();
          onOpenAsset(linked);
        }}
      >
        {linked.name}
      </button>
    );
  }

  return (
    <span className="muted" style={linkedStyle}>
      Broken link
    </span>
  );
}

function DomainPill({ domain }: { domain: AssetDomain }) {
  return (
    <span
      className={`assetRegistryDomainPill ${
        domain === 'business' ? 'assetRegistryDomainPillBusiness' : 'assetRegistryDomainPillPersonal'
      }`}
    >
      {domainLabel(domain)}
    </span>
  );
}

export function FinancialsView() {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const [searchParams] = useSearchParams();
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const { financialItems: items, setFinancialItems: setItems } = useAppData();
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');
  const [filter, setFilter] = useState<Filter>('all');
  const { sortKey, sortDirection, toggleSort, sortRows } = useTableSort<FinancialSortKey>(
    'name',
    'asc',
  );
  const [formItem, setFormItem] = useState<FinancialItem | null | undefined>(undefined);
  const [sidebarTabIndex, setSidebarTabIndex] = useState(0);
  const [documentsItemId, setDocumentsItemId] = useState('');
  const [documentUploadControl, setDocumentUploadControl] =
    useState<EntityDocumentUploadControl | null>(null);

  const domainFilteredItems = useMemo(
    () => filterByDomain(items, domainFilter),
    [items, domainFilter],
  );

  const filtered = useMemo(() => {
    const byType =
      filter === 'all'
        ? domainFilteredItems
        : domainFilteredItems.filter((i) => i.type === filter);
    return sortRows(byType, {
      name: (i) => i.name,
      domain: (i) => itemDomain(i),
      category: (i) => itemCategoryLabel(i),
      value: (i) => i.currentValue,
      monthlyChange: (i) => i.monthlyChange,
      rating: (i) => RATING_SORT_ORDER[i.rating] ?? 0,
    });
  }, [domainFilteredItems, filter, sortRows]);

  const summary = useMemo(
    () => financialPositionSummary(domainFilteredItems),
    [domainFilteredItems],
  );

  const assetCount = useMemo(
    () => domainFilteredItems.filter((i) => i.type === 'asset').length,
    [domainFilteredItems],
  );
  const liabilityCount = useMemo(
    () => domainFilteredItems.filter((i) => i.type === 'liability').length,
    [domainFilteredItems],
  );

  const monthlyNetChange = useMemo(
    () => domainFilteredItems.reduce((sum, item) => sum + item.monthlyChange, 0),
    [domainFilteredItems],
  );

  const topAsset = useMemo(() => {
    if (summary.assets <= 0) return null;
    let maxValue = 0;
    let maxName = '';
    for (const item of domainFilteredItems) {
      if (item.type !== 'asset') continue;
      if (item.currentValue > maxValue) {
        maxValue = item.currentValue;
        maxName = item.name;
      }
    }
    if (maxValue <= 0) return null;
    return { name: maxName, share: (maxValue / summary.assets) * 100, value: maxValue };
  }, [domainFilteredItems, summary.assets]);

  const debtRatio = summary.assets > 0 ? (summary.liabilities / summary.assets) * 100 : null;

  const atRiskCount = useMemo(
    () =>
      domainFilteredItems.filter((i) => i.rating === 'critical' || i.rating === 'bad').length,
    [domainFilteredItems],
  );

  const domainMix = useMemo(() => {
    const personal = items.filter((i) => itemDomain(i) === 'personal').length;
    const business = items.filter((i) => itemDomain(i) === 'business').length;
    return { personal, business };
  }, [items]);

  const statHints = useMemo(() => {
    const n = domainFilteredItems.length;
    const scope =
      domainFilter === 'all'
        ? 'Across personal and business'
        : `${domainLabel(domainFilter)} holdings only`;

    return {
      assets:
        n > 0
          ? `${assetCount} ${assetCount === 1 ? 'asset' : 'assets'} · ${scope.toLowerCase()}`
          : 'Homes, cash, securities, and other holdings',
      liabilities:
        n > 0
          ? `${liabilityCount} ${liabilityCount === 1 ? 'liability' : 'liabilities'} · ${scope.toLowerCase()}`
          : 'Mortgages, loans, and other obligations',
      net:
        summary.net >= 0
          ? 'Assets minus liabilities — your balance-sheet headline'
          : 'Negative net means liabilities outweigh assets on paper',
      items:
        n > 0
          ? `${assetCount} assets · ${liabilityCount} liabilities`
          : 'Add items to populate your registry',
      monthly:
        n > 0
          ? 'Sum of monthly Δ across every line item'
          : 'Track monthly changes to see cash-flow drift',
      topAsset: topAsset
        ? `${topAsset.name} · ${formatEuro(topAsset.value, true)}`
        : n > 0
          ? 'Largest asset by current value'
          : 'Concentration shows up once you add assets',
      debtRatio:
        debtRatio != null
          ? 'Liabilities ÷ assets — lower is generally healthier'
          : 'Add assets to benchmark leverage',
      atRisk:
        atRiskCount > 0
          ? 'Items rated bad or critical — worth a closer look'
          : n > 0
            ? 'No items flagged bad or critical'
            : 'Ratings surface items that need attention',
      domains:
        domainFilter !== 'all'
          ? `Showing ${domainLabel(domainFilter).toLowerCase()} only — switch filter to compare`
          : domainMix.personal + domainMix.business > 0
            ? `${domainMix.personal} personal · ${domainMix.business} business`
            : 'Tag items personal or business to split the book',
    };
  }, [
    domainFilteredItems.length,
    domainFilter,
    assetCount,
    liabilityCount,
    summary.net,
    topAsset,
    debtRatio,
    atRiskCount,
    domainMix,
  ]);

  const kpiExplains = useMemo(
    () =>
      buildFinancialsKpiExplains({
        items: domainFilteredItems,
        summary,
        assetCount,
        liabilityCount,
        topAsset,
        debtRatio,
        atRiskCount,
        domainFilter,
        domainMix,
      }),
    [
      domainFilteredItems,
      summary,
      assetCount,
      liabilityCount,
      topAsset,
      debtRatio,
      atRiskCount,
      domainFilter,
      domainMix,
    ],
  );

  const assetsById = useMemo(() => assetsByIdMap(items), [items]);

  const documentItemOptions: GlobalDropdownOption[] = useMemo(
    () =>
      [...items]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item) => ({
          value: item.id,
          label: `${item.name} (${item.type === 'asset' ? 'Asset' : 'Liability'})`,
        })),
    [items]
  );

  const documentsItem = useMemo(
    () => items.find((item) => item.id === documentsItemId) ?? null,
    [items, documentsItemId]
  );

  useEffect(() => {
    const itemParam = searchParams.get('item')?.trim() ?? '';
    if (!itemParam) return;

    const match = items.find((item) => item.id === itemParam);
    if (!match) return;

    setDocumentsItemId(match.id);

    if (searchParams.get('tab') === 'documents') {
      setFormItem(match);
      setSidebarTabIndex(1);
    }
  }, [items, searchParams]);

  function saveItem(next: FinancialItem) {
    const id = next.id?.trim() ? next.id : newId();
    const merged = { ...next, id };
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx === -1) return [...prev, merged];
      const copy = [...prev];
      copy[idx] = merged;
      return copy;
    });
    closeItemSidebar();
  }

  function deleteItem(id: string) {
    setItems((prev) => prev.filter((p) => p.id !== id));
    closeItemSidebar();
  }

  function openAddItem() {
    setSidebarTabIndex(0);
    setFormItem(null);
  }

  function openEdit(item: FinancialItem) {
    setSidebarTabIndex(0);
    setFormItem(item);
    setDocumentsItemId(item.id);
  }

  function closeItemSidebar() {
    setFormItem(undefined);
    setSidebarTabIndex(0);
  }

  const summaryScopeLabel =
    domainFilter === 'all' ? 'all domains' : domainLabel(domainFilter);

  const addItemAction = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        onClick={openAddItem}
        icon={
          <img
            src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        Add item
      </GlobalButton>
    </>
  );

  const summarySection = (
    <>
      <ViewSectionHeader
        eyebrow="Assets & liabilities"
        title="Summary"
        subtitle={`Balance-sheet snapshot for ${summaryScopeLabel.toLowerCase()}.`}
      />

      <SortableStatGrid
        viewId="financials"
        className="grid-stats grid-stats-2"
        ariaLabel={`Financial summary for ${summaryScopeLabel}`}
        cards={[
          {
            id: 'net',
            pinned: true,
            label: 'Net position',
            node: (
              <StatCard
                netWorthHeadline
                label="Net position"
                emoji="🏦"
                value={assetValueNode(summary.net)}
                hint={statHints.net}
                valueWrap
                kpiExplain={kpiExplains.net}
              />
            ),
          },
          {
            id: 'assets',
            label: 'Total assets',
            node: (
              <StatCard
                label="Total assets"
                emoji="💰"
                value={assetValueNode(summary.assets)}
                hint={statHints.assets}
                valueWrap
                kpiExplain={kpiExplains.assets}
              />
            ),
          },
          {
            id: 'liabilities',
            label: 'Total liabilities',
            node: (
              <StatCard
                label="Total liabilities"
                emoji="📉"
                value={liabilityValueNode(summary.liabilities)}
                hint={statHints.liabilities}
                valueWrap
                kpiExplain={kpiExplains.liabilities}
              />
            ),
          },
          {
            id: 'items',
            label: 'Registry items',
            node: (
              <StatCard
                label="Registry items"
                emoji="📋"
                value={`${assetCount}/${liabilityCount}`}
                hint={statHints.items}
                kpiExplain={kpiExplains.items}
              />
            ),
          },
          {
            id: 'monthly',
            label: 'Monthly net change',
            node: (
              <StatCard
                label="Monthly net change"
                emoji="📊"
                value={signedChangeNode(monthlyNetChange)}
                hint={statHints.monthly}
                valueWrap
                kpiExplain={kpiExplains.monthly}
              />
            ),
          },
          {
            id: 'top-asset',
            label: 'Largest asset',
            node: (
              <StatCard
                label="Largest asset"
                emoji="👑"
                value={topAsset ? formatPercentPoints(topAsset.share) : '—'}
                hint={statHints.topAsset}
                kpiExplain={kpiExplains['top-asset']}
              />
            ),
          },
          {
            id: 'debt-ratio',
            label: 'Debt ratio',
            node: (
              <StatCard
                label="Debt ratio"
                emoji="⚖️"
                value={debtRatio != null ? formatPercentPoints(debtRatio) : '—'}
                hint={statHints.debtRatio}
                kpiExplain={kpiExplains['debt-ratio']}
              />
            ),
          },
          {
            id: 'at-risk',
            label: 'Items at risk',
            node: (
              <StatCard
                label="Items at risk"
                emoji="🚨"
                value={domainFilteredItems.length > 0 ? String(atRiskCount) : '—'}
                hint={statHints.atRisk}
                kpiExplain={kpiExplains['at-risk']}
              />
            ),
          },
          {
            id: 'domains',
            label: 'Domains',
            node: (
              <StatCard
                label="Domains"
                emoji="🏠"
                value={
                  domainFilter !== 'all'
                    ? domainLabel(domainFilter)
                    : domainMix.personal + domainMix.business > 0
                      ? `${domainMix.personal + domainMix.business}`
                      : '—'
                }
                hint={statHints.domains}
                kpiExplain={kpiExplains.domains}
              />
            ),
          },
        ]}
      />
    </>
  );

  const registrySection = (
    <>
      <ViewSectionHeader
        eyebrow="Assets & liabilities"
        title="Registry"
        subtitle="Personal and business line items in one list."
      />

      <div
        className="toolbar"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="segmented" role="tablist" aria-label="Filter domain">
            {(['all', 'personal', 'business'] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={domainFilter === k}
                className={domainFilter === k ? 'active' : ''}
                onClick={() => setDomainFilter(k)}
              >
                {k === 'all' ? 'All' : domainLabel(k)}
              </button>
            ))}
          </div>
          <div className="segmented" role="tablist" aria-label="Filter items">
            {(['all', 'asset', 'liability'] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={filter === k}
                className={filter === k ? 'active' : ''}
                onClick={() => setFilter(k)}
              >
                {k === 'all' ? 'All' : k === 'asset' ? 'Assets' : 'Liabilities'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section>
        {filtered.length === 0 ? (
          domainFilteredItems.length === 0 ? (
            items.length === 0 ? (
              <div className="empty-state">
                <p className="muted">{globalEmptyLeadCopy(domainFilter)}</p>
                <GlobalEmptyState buttonText="Add asset" onButtonClick={openAddItem} />
              </div>
            ) : (
              <div className="empty-state">
                <p>{domainEmptyCopy(domainFilter as Exclude<DomainFilter, 'all'>)}</p>
                <GlobalButton type="button" variant="primary" onClick={openAddItem}>
                  Add asset
                </GlobalButton>
                <GlobalButton type="button" variant="secondary" onClick={() => setDomainFilter('all')}>
                  Show all domains
                </GlobalButton>
              </div>
            )
          ) : (
            <div className="empty-state">
              <p>{filterEmptyCopy(domainFilter, filter)}</p>
              <GlobalButton type="button" variant="secondary" onClick={() => setFilter('all')}>
                Show all
              </GlobalButton>
            </div>
          )
        ) : (
          <>
            <div className="table-card assetRegistryListDesktop">
              <div className="table-scroll">
                <table className="data-table assetRegistryTable">
                  <thead>
                    <tr>
                      <GlobalSortableTh sortKey="name" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Name
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="domain" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Domain
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="category" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Category
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="value" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Value
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="monthlyChange" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Monthly Δ
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="rating" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Rating
                      </GlobalSortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item) => (
                      <tr
                        key={item.id}
                        className="click-row"
                        onClick={() => openEdit(item)}
                      >
                        <td className="assetRegistryNameCell">
                          <div>{item.name}</div>
                          <LinkedAssetLine
                            item={item}
                            assetsById={assetsById}
                            onOpenAsset={openEdit}
                          />
                        </td>
                        <td>
                          <DomainPill domain={itemDomain(item)} />
                        </td>
                        <td>{itemCategoryLabel(item)}</td>
                        <td>{registryItemValueNode(item)}</td>
                        <td>{signedChangeNode(item.monthlyChange)}</td>
                        <td>
                          <span className={ratingClass(item.rating)}>{item.rating}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="assetRegistryListMobile" role="list">
              {filtered.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="assetRegistryCard"
                  role="listitem"
                  onClick={() => openEdit(item)}
                >
                  <div className="assetRegistryCardTop">
                    <div className="assetRegistryCardMain">
                      <DomainPill domain={itemDomain(item)} />
                      <h3 className="assetRegistryCardName">{item.name}</h3>
                      <LinkedAssetLine
                        item={item}
                        assetsById={assetsById}
                        onOpenAsset={openEdit}
                      />
                      <p className="assetRegistryCardCategory muted">{itemCategoryLabel(item)}</p>
                    </div>
                    <div className="assetRegistryCardValues">
                      <div className="assetRegistryCardValue">{registryItemValueNode(item)}</div>
                      <div className="assetRegistryCardDelta">
                        {signedChangeNode(item.monthlyChange)} / mo
                      </div>
                    </div>
                  </div>
                  <div className="assetRegistryCardFooter">
                    <span className={ratingClass(item.rating)}>{item.rating}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );

  const sections: ViewSection[] = [
    { id: 'summary', label: 'Summary', content: summarySection },
    { id: 'registry', label: 'Registry', content: registrySection },
    {
      id: 'documents',
      label: 'Documents',
      content: (
        <>
          <ViewSectionHeader
            eyebrow="Asset registry"
            title="Documents"
            subtitle="Attach statements, contracts, and exports to registry items."
            actions={
              items.length > 0 ? (
                <GlobalButton
                  type="button"
                  variant="secondary"
                  isLoading={documentUploadControl?.uploading}
                  disabled={!documentUploadControl || documentUploadControl.uploading}
                  onClick={() => documentUploadControl?.openFilePicker()}
                  icon={
                    <img
                      src={
                        isDarkMode
                          ? '/assets/icons/icloud.and.arrow.up.svg'
                          : '/assets/icons/icloud.and.arrow.up.black.svg'
                      }
                      alt=""
                      width={16}
                      height={16}
                    />
                  }
                >
                  Upload document
                </GlobalButton>
              ) : null
            }
          />
          {items.length === 0 ? (
            <div className="table-card">
              <p className="muted" style={{ margin: 0 }}>
                Add an asset or liability first, then attach supporting documents here.
              </p>
            </div>
          ) : (
            <>
              <div style={{ maxWidth: 420, marginBottom: 16 }}>
                <GlobalDropdown
                  label="Registry item"
                  value={documentsItemId}
                  options={documentItemOptions}
                  onChange={(value) => setDocumentsItemId(value)}
                />
              </div>
              {documentsItem && userId ? (
                <EntityDocumentsPanel
                  userId={userId}
                  entityType="financial_item"
                  entityId={documentsItem.id}
                  entityLabel={documentsItem.name}
                  onUploadControlChange={setDocumentUploadControl}
                />
              ) : (
                <p className="muted">Select an item to manage its documents.</p>
              )}
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <ViewPage title="Asset & Liabilities" actions={addItemAction}>
      <ViewSections mode={layoutMode} sections={sections} />

      <GlobalSidebar isOpen={formItem !== undefined} onClose={closeItemSidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">{formItem ? 'Edit item' : 'New item'}</h2>
          {formItem?.id && userId ? (
            <GlobalTabs
              initialIndex={sidebarTabIndex}
              onChange={setSidebarTabIndex}
              tabs={[
                {
                  label: 'Details',
                  content: (
                    <FinancialItemForm
                      item={formItem}
                      onSubmit={saveItem}
                      onCancel={closeItemSidebar}
                      onDelete={() => deleteItem(formItem.id)}
                    />
                  ),
                },
                {
                  label: 'Documents',
                  content: (
                    <EntityDocumentsPanel
                      userId={userId}
                      entityType="financial_item"
                      entityId={formItem.id}
                      entityLabel={formItem.name}
                      compact
                    />
                  ),
                },
              ]}
            />
          ) : (
            <FinancialItemForm
              item={formItem ?? undefined}
              onSubmit={saveItem}
              onCancel={closeItemSidebar}
              onDelete={formItem ? () => deleteItem(formItem.id) : undefined}
            />
          )}
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
